import type { ProductType } from 'spacetimedb';
import type { ReducerEventContext } from '../generated';
import { logReducerCall } from '../reducerLogger';
import type { ConnectionManager } from './ConnectionManager';
import type { Logger } from '../types';

export interface ReducerConfirmationOptions<TArgs extends Record<string, unknown>> {
  readonly reducerName: string;
  readonly paramsType: ProductType;
  readonly args: TArgs;
  readonly onEvent: string;
  readonly offEvent: string;
  readonly matchArgs?: (args: TArgs) => boolean;
  readonly timeoutMessage: string;
  readonly failureMessage: string;
  readonly timeoutMs?: number;
}

export class ReducerClient {
  constructor(
    private readonly connectionManager: ConnectionManager,
    private readonly logger: Logger = () => undefined
  ) {}

  public async callAndConfirm<TArgs extends Record<string, unknown>>(
    options: ReducerConfirmationOptions<TArgs>
  ): Promise<void> {
    try {
      await this.callFireAndForget(options.reducerName, options.paramsType, options.args);
      logReducerCall(options.reducerName, 'success');
    } catch (error) {
      const message = error instanceof Error && error.message.length > 0
        ? error.message
        : options.failureMessage;
      logReducerCall(options.reducerName, 'error', message);
      throw new Error(message);
    }
  }

  public async callFireAndForget(
    reducerName: string,
    paramsType: ProductType,
    args: Record<string, unknown>
  ): Promise<void> {
    const connection = await this.connectionManager.ensureConnection();
    const serializedArgs = this.formatReducerArgs(paramsType, args);
    await connection.callReducerWithParams(reducerName, paramsType, serializedArgs);
  }

  private formatReducerArgs(paramsType: ProductType, args: Record<string, unknown>): Record<string, unknown> {
    const elements = paramsType.elements ?? [];
    if (elements.length === 1 && elements[0]?.name === 'args') {
      return { args };
    }
    return args;
  }
}
